import { Link } from 'react-router-dom';
import { ClipboardPlus, Eye, ImageOff, Ruler } from 'lucide-react';
import { useSolicitation } from '../context/SolicitationContext';
import { getCategory } from '../data/catalog';
import { availableVariants, isProductVisible, isVariantless } from '../utils/availability';
import { resolveVariantImage, resolveProductImage } from '../utils/photoStatus';

export default function ProductCard({ product, index = 0, variantMatch = null }) {
  const { addItem } = useSolicitation();
  const cat = getCategory(product.categorySlug);
  if (!isProductVisible(product)) return null;
  const variantless = isVariantless(product);
  const avail = availableVariants(product);
  const first = avail[0];
  const multi = avail.length > 1;
  const imgSrc = product.coverImage || (variantless ? resolveProductImage(product) : resolveVariantImage(first));
  return (
    <article
      className="group flex flex-col"
      data-testid={`product-card-${product.slug}`}
      style={{ animationDelay: `${(index % 6) * 60}ms` }}
    >
      <Link
        to={`/produto/${product.slug}`}
        className="relative block overflow-hidden rounded-xl bg-muted"
        data-testid={`product-card-link-${product.slug}`}
        aria-label={`Ver ${product.name}`}
      >
        {imgSrc ? (
          <>
            <img
              src={imgSrc}
              alt={first ? `${product.name} - ${first.name}` : product.name}
              loading="lazy"
              className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            />
            {product.coverImage && imgSrc === product.coverImage && (
              <span
                className="absolute bottom-1.5 left-1.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-white/90"
                data-testid={`product-card-illustrative-${product.slug}`}
              >
                Foto ilustrativa
              </span>
            )}
          </>
        ) : (
          <div
            className="flex aspect-square w-full flex-col items-center justify-center gap-2 bg-navy-light text-navy/50"
            data-testid={`product-card-photo-pending-${product.slug}`}
          >
            <ImageOff className="h-8 w-8" strokeWidth={1.5} />
            <span className="text-[11px] font-semibold uppercase tracking-wide">Foto em atualização</span>
          </div>
        )}
      </Link>
      <div className="flex flex-1 flex-col pt-2.5">
        <Link to={`/produto/${product.slug}`} className="block min-h-[2.5rem] text-base font-medium leading-snug text-foreground hover:text-navy">
          {product.name}
        </Link>
        {cat && <p className="truncate text-sm text-muted-foreground">{cat.name}</p>}
        {variantless ? (
          <p className="mt-0.5 min-h-[1.25rem] truncate text-sm text-muted-foreground" data-testid={`product-card-unit-${product.slug}`}>
            Vendido por metro
          </p>
        ) : multi ? (
          <p className="mt-0.5 min-h-[1.25rem] truncate text-sm font-medium text-navy" data-testid={`product-card-options-${product.slug}`}>
            {avail.length} {avail.length === 1 ? 'opção disponível' : 'opções disponíveis'}
          </p>
        ) : (
          <p className="mt-0.5 min-h-[1.25rem] truncate text-sm text-muted-foreground">{first.name}</p>
        )}
        {variantMatch && (
          <p className="mt-1 text-xs font-semibold text-navy" data-testid={`product-card-variant-match-${product.slug}`}>
            Variante correspondente: {variantMatch}
          </p>
        )}
        <div className="mt-auto pt-2.5">
          {variantless ? (
            <Link
              to={`/produto/${product.slug}`}
              data-testid={`product-card-choose-meters-button-${product.slug}`}
              className="flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-navy px-2 text-center text-[11px] font-semibold leading-tight text-white transition-colors hover:bg-navy-hover md:h-auto md:gap-2 md:px-4 md:py-2.5 md:text-xs md:tracking-wide"
            >
              <Ruler className="h-3.5 w-3.5 shrink-0 md:h-4 md:w-4" />
              ESCOLHER METRAGEM
            </Link>
          ) : multi ? (
            <Link
              to={`/produto/${product.slug}`}
              data-testid={`product-card-view-options-button-${product.slug}`}
              className="flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-navy px-2 text-center text-[11px] font-semibold leading-tight text-white transition-colors hover:bg-navy-hover md:h-auto md:gap-2 md:px-4 md:py-2.5 md:text-xs md:tracking-wide"
            >
              <Eye className="h-3.5 w-3.5 shrink-0 md:h-4 md:w-4" />
              VER OPÇÕES
            </Link>
          ) : (
            <button
              onClick={() => addItem(product, first, 1)}
              data-testid={`product-card-add-request-button-${product.slug}`}
              className="flex h-11 w-full items-center justify-center gap-1.5 rounded-full border border-navy px-2 text-center text-[11px] font-semibold leading-tight text-navy transition-colors hover:bg-navy hover:text-white md:h-auto md:gap-2 md:px-4 md:py-2.5 md:text-xs md:tracking-wide"
            >
              <ClipboardPlus className="h-3.5 w-3.5 shrink-0 md:h-4 md:w-4" />
              ADICIONAR À SOLICITAÇÃO
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
